import {useEffect, useMemo, useRef, useState, useCallback} from "react";
import { Button } from "@heroui/button";
import { Drawer, DrawerContent, DrawerBody, DrawerFooter } from "@heroui/drawer";
import {ChevronUp, ChevronDown, Clock} from "lucide-react";

export default function TimeWheelDrawer({
  value = "",
  onSelect = () => {},
  minuteStep = 15,
  use12h = false,
  placeholder = "Select time",
  disabled = false,
  className = "",
  showFooter = true,
}) {
  const [open, setOpen] = useState(false);

  const parsed = useMemo(() => parseTime(value), [value]);
  const initial = parsed ?? { h24: 9, m: 0 };

  const ITEM_HEIGHT = 44;
  const VISIBLE_H = ITEM_HEIGHT * 3;
  const COL_PAD = ITEM_HEIGHT;

  const hourColRef = useRef(null);
  const minuteColRef = useRef(null);
  const ampmColRef = useRef(null);

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, minuteStep);
    const arr = [];
    for (let m = 0; m < 60; m += step) arr.push(m);
    if (60 % step !== 0 && arr[arr.length - 1] !== 59) arr.push(59);
    return arr;
  }, [minuteStep]);

  const hourOptions = useMemo(() => {
    return use12h ? Array.from({ length: 12 }, (_, i) => i + 1)
                  : Array.from({ length: 24 }, (_, i) => i);
  }, [use12h]);

  const ampmOptions = ["AM", "PM"];

  const [selHourIdx, setSelHourIdx] = useState(0);
  const [selMinuteIdx, setSelMinuteIdx] = useState(0);
  const [selAmpmIdx, setSelAmpmIdx] = useState(0);

  const pad2 = (n) => String(n).padStart(2, "0");

  const indexFromValue = useCallback((v, list) => {
    const i = list.indexOf(v);
    return i >= 0 ? i : 0;
  }, []);

  const valueFromScroll = useCallback((el, list) => {
    if (!el) return { idx: 0, val: list[0] };
    const rawIdx = Math.round(el.scrollTop / ITEM_HEIGHT);
    const idx = Math.max(0, Math.min(list.length - 1, rawIdx));
    return { idx, val: list[idx] };
  }, []);

  const scrollToIndex = useCallback((el, idx) => {
    if (!el) return;
    el.scrollTo({ top: idx * ITEM_HEIGHT, behavior: "smooth" });
  }, []);

  const commitSelection = useCallback((hIdx, mIdx, aIdx) => {
    const hourVal = hourOptions[hIdx] ?? hourOptions[0];
    const minuteVal = minuteOptions[mIdx] ?? minuteOptions[0];

    let h24 = hourVal;
    if (use12h) {
      const mer = ampmOptions[aIdx] ?? "AM";
      const isPM = mer === "PM";
      h24 = (hourVal % 12) + (isPM ? 12 : 0);
    }
    onSelect(`${pad2(h24)}:${pad2(minuteVal)}`);
  }, [hourOptions, minuteOptions, use12h, ampmOptions, onSelect]);

  useEffect(() => {
    if (!open) return;
    let h24 = initial.h24;
    let m = findNearest(minuteOptions, initial.m);

    if (use12h) {
      const isPM = h24 >= 12;
      let hr12 = h24 % 12;
      if (hr12 === 0) hr12 = 12;

      const hi = indexFromValue(hr12, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      const ai = isPM ? 1 : 0;

      setSelHourIdx(hi); setSelMinuteIdx(mi); setSelAmpmIdx(ai);
      setTimeout(() => {
        scrollToIndex(hourColRef.current, hi);
        scrollToIndex(minuteColRef.current, mi);
        scrollToIndex(ampmColRef.current, ai);
      }, 60);
    } else {
      const hi = indexFromValue(h24, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      setSelHourIdx(hi); setSelMinuteIdx(mi);
      setTimeout(() => {
        scrollToIndex(hourColRef.current, hi);
        scrollToIndex(minuteColRef.current, mi);
      }, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hourTimerRef = useRef(null);
  const minuteTimerRef = useRef(null);
  const ampmTimerRef = useRef(null);
  const DEBOUNCE_MS = 120;

  const onScrollHours = useCallback((e) => {
    const el = e.currentTarget;
    const { idx } = valueFromScroll(el, hourOptions);
    setSelHourIdx(idx);
    if (hourTimerRef.current) clearTimeout(hourTimerRef.current);
    hourTimerRef.current = setTimeout(() => {
      const { idx: finalIdx } = valueFromScroll(el, hourOptions);
      scrollToIndex(hourColRef.current, finalIdx);
      setSelHourIdx(finalIdx);
      commitSelection(finalIdx, selMinuteIdx, selAmpmIdx);
    }, DEBOUNCE_MS);
  }, [hourOptions, valueFromScroll, scrollToIndex, commitSelection, selMinuteIdx, selAmpmIdx]);

  const onScrollMinutes = useCallback((e) => {
    const el = e.currentTarget;
    const { idx } = valueFromScroll(el, minuteOptions);
    setSelMinuteIdx(idx);
    if (minuteTimerRef.current) clearTimeout(minuteTimerRef.current);
    minuteTimerRef.current = setTimeout(() => {
      const { idx: finalIdx } = valueFromScroll(el, minuteOptions);
      scrollToIndex(minuteColRef.current, finalIdx);
      setSelMinuteIdx(finalIdx);
      commitSelection(selHourIdx, finalIdx, selAmpmIdx);
    }, DEBOUNCE_MS);
  }, [minuteOptions, valueFromScroll, scrollToIndex, commitSelection, selHourIdx, selAmpmIdx]);

  const onScrollAmpm = useCallback((e) => {
    const el = e.currentTarget;
    const { idx } = valueFromScroll(el, ampmOptions);
    setSelAmpmIdx(idx);
    if (ampmTimerRef.current) clearTimeout(ampmTimerRef.current);
    ampmTimerRef.current = setTimeout(() => {
      const { idx: finalIdx } = valueFromScroll(el, ampmOptions);
      scrollToIndex(ampmColRef.current, finalIdx);
      setSelAmpmIdx(finalIdx);
      commitSelection(selHourIdx, selMinuteIdx, finalIdx);
    }, DEBOUNCE_MS);
  }, [ampmOptions, valueFromScroll, scrollToIndex, commitSelection, selHourIdx, selMinuteIdx]);

  const handleConfirm = () => {
    commitSelection(selHourIdx, selMinuteIdx, selAmpmIdx);
    setOpen(false);
  };

  const setNow = () => {
    const now = new Date();
    const h = now.getHours();
    const m = findNearest(minuteOptions, now.getMinutes());

    if (use12h) {
      const isPM = h >= 12;
      let hr12 = h % 12;
      if (hr12 === 0) hr12 = 12;
      const hi = indexFromValue(hr12, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      const ai = isPM ? 1 : 0;
      setSelHourIdx(hi); setSelMinuteIdx(mi); setSelAmpmIdx(ai);
      scrollToIndex(hourColRef.current, hi);
      scrollToIndex(minuteColRef.current, mi);
      scrollToIndex(ampmColRef.current, ai);
      commitSelection(hi, mi, ai);
    } else {
      const hi = indexFromValue(h, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      setSelHourIdx(hi); setSelMinuteIdx(mi);
      scrollToIndex(hourColRef.current, hi);
      scrollToIndex(minuteColRef.current, mi);
      commitSelection(hi, mi, selAmpmIdx);
    }
  };

  // Column renderer (improved light-mode contrast)
  const renderColumn = (ref, items, selectedIdx, onScroll, fmt = (v) => v) => (
    <div className="relative flex-1" style={{ height: VISIBLE_H }}>
      <div
        ref={ref}
        onScroll={onScroll}
        className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory scroll-smooth will-change-scroll overscroll-contain"
        style={{
          paddingTop: COL_PAD,
          paddingBottom: COL_PAD,
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
        }}
      >
        {items.map((v, i) => (
          <div
            key={`${v}-${i}`}
            className="snap-center flex items-center justify-center select-none"
            style={{ height: ITEM_HEIGHT }}
          >
            <span
              className={[
                "transition-all duration-120",
                i === selectedIdx
                  ? "opacity-100 font-medium text-base scale-[1.06] text-neutral-900 dark:text-white"
                  : "text-sm text-neutral-600 dark:text-white/60"
              ].join(" ")}
            >
              {fmt(v)}
            </span>
          </div>
        ))}
      </div>

      {/* Center guide lines: crisper in light */}
      <div
        className="pointer-events-none absolute left-0 right-0"
        style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
      >
        <div className="relative h-full">
          <div className="absolute inset-x-2 top-0 h-px bg-neutral-200 dark:bg-default-200/80" />
          <div className="absolute inset-x-2 bottom-0 h-px bg-neutral-200 dark:bg-default-200/80" />
        </div>
      </div>
    </div>
  );

  const displayLabel = value ? (use12h ? to12h(value) : value) : "";

  return (
    <>
      {/* Trigger — solid in light, glass in dark */}
      <button
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={[
          "w-full inline-flex items-center justify-between gap-3 rounded-xl",
          "border border-neutral-200 dark:border-default-200/70",
          "bg-white hover:bg-neutral-50 dark:bg-content1/50 dark:hover:bg-content2/60",
          "px-3 py-2 text-left transition-colors shadow-sm",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
          className,
        ].join(" ")}
      >
        <span className="inline-flex items-center gap-2 text-neutral-800 dark:text-default-700">
          <Clock className="h-4 w-4 text-neutral-600 dark:opacity-70" />
          <span className={displayLabel ? "" : "opacity-60"}>
            {displayLabel || placeholder}
          </span>
        </span>
        <span className="flex flex-col items-center justify-center leading-none">
          <ChevronUp className="h-4 w-4 -mb-0.5 text-neutral-500 dark:opacity-60" />
          <ChevronDown className="h-4 w-4 text-neutral-500 dark:opacity-60" />
        </span>
      </button>

      <Drawer
        isOpen={open}
        onOpenChange={setOpen}
        placement="bottom"
        backdrop="blur"
        size="lg"
        classNames={{
          base: "max-h-[70vh] bg-transparent",
          backdrop: "bg-black/20 dark:bg-black/60 backdrop-blur-0 dark:backdrop-blur-md",
        }}
      >
        {/* Panel — solid white in light, glass in dark */}
        <DrawerContent className="
          bg-white dark:bg-content1/70
          backdrop-blur-0 dark:backdrop-blur-xl
          border-t border-neutral-200 dark:border-default-200/60
          rounded-t-2xl shadow-2xl dark:shadow-[0_-12px_30px_rgba(0,0,0,0.4)]
        ">
          <DrawerBody className="pt-4">
            <div className="flex items-stretch gap-3 px-1">
              {renderColumn(
                hourColRef,
                hourOptions,
                selHourIdx,
                onScrollHours,
                (v) => (use12h ? v : pad2(v))
              )}
              {renderColumn(
                minuteColRef,
                minuteOptions,
                selMinuteIdx,
                onScrollMinutes,
                (v) => pad2(v)
              )}
              {use12h &&
                renderColumn(
                  ampmColRef,
                  ampmOptions,
                  selAmpmIdx,
                  onScrollAmpm,
                  (v) => v
                )}
            </div>
          </DrawerBody>

          {showFooter && (
            <DrawerFooter className="border-t border-neutral-200 dark:border-default-200/50 h-18"></DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

/* ---------- utils ---------- */
function findNearest(list, value) {
  let best = list[0], diff = Infinity;
  for (const v of list) {
    const d = Math.abs(v - value);
    if (d < diff) { best = v; diff = d; }
  }
  return best;
}

function parseTime(str) {
  if (!str || typeof str !== "string") return null;
  const m24 = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(str.trim());
  if (m24) return { h24: +m24[1], m: +m24[2] };
  const m12 = /^([1-9]|1[0-2]):([0-5]\d)\s*([AP]M)$/i.exec(str.trim());
  if (m12) {
    let h = +m12[1], m = +m12[2];
    const mer = m12[3].toUpperCase();
    if (mer === "PM" && h !== 12) h += 12;
    if (mer === "AM" && h === 12) h = 0;
    return { h24: h, m };
  }
  return null;
}

function to12h(hhmm) {
  const p = parseTime(hhmm);
  if (!p) return hhmm;
  const h12 = ((p.h24 + 11) % 12) + 1;
  const mer = p.h24 >= 12 ? "PM" : "AM";
  return `${h12}:${String(p.m).padStart(2,"0")} ${mer}`;
}